import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Sparkles, Zap, Crown, Building2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const plans = [
  {
    id: 'free',
    name: 'Free',
    description: 'Perfect for getting started',
    price: 0,
    period: 'forever',
    icon: Sparkles,
    color: 'gray',
    features: [
      '1 Website',
      '10 Products',
      '100 MB Storage',
      '3 Pages per site',
      'Basic Templates',
      'Community Support',
    ],
    notIncluded: [
      'Custom Domain',
      'AI Content',
      'Analytics',
      'Priority Support',
    ],
  },
  {
    id: 'starter',
    name: 'Starter',
    description: 'For small businesses',
    price: 9,
    period: 'month',
    icon: Zap,
    color: 'blue',
    popular: true,
    features: [
      '3 Websites',
      '50 Products',
      '500 MB Storage',
      '10 Pages per site',
      'All Templates',
      'Custom Domain',
      'Basic SEO',
      'Email Support',
    ],
    notIncluded: [
      'AI Content',
      'Advanced Analytics',
    ],
  },
  {
    id: 'professional',
    name: 'Professional',
    description: 'For growing businesses',
    price: 29,
    period: 'month',
    icon: Crown,
    color: 'purple',
    features: [
      '10 Websites',
      '500 Products',
      '2 GB Storage',
      '50 Pages per site',
      'All Templates',
      'Custom Domain',
      'Advanced SEO',
      'AI Content Generation',
      'Full Analytics',
      'Priority Support',
    ],
    notIncluded: [],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'For large organizations',
    price: 99,
    period: 'month',
    icon: Building2,
    color: 'indigo',
    features: [
      'Unlimited Websites',
      'Unlimited Products',
      '10 GB Storage',
      'Unlimited Pages',
      'All Templates',
      'Custom Domain',
      'Advanced SEO',
      'AI Content Generation',
      'Full Analytics',
      'White Label',
      'API Access',
      'Team Collaboration',
      '24/7 Priority Support',
    ],
    notIncluded: [],
  },
];

const paymentGateways = [
  { id: 'stripe', name: 'Stripe', color: 'bg-purple-600' },
  { id: 'paypal', name: 'PayPal', color: 'bg-blue-600' },
  { id: 'razorpay', name: 'Razorpay', color: 'bg-blue-500' },
  { id: 'bkash', name: 'bKash', color: 'bg-pink-500' },
  { id: 'nagad', name: 'Nagad', color: 'bg-orange-500' },
  { id: 'upay', name: 'Upay', color: 'bg-green-500' },
];

const Plans = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedGateway, setSelectedGateway] = useState('stripe');
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const handleSelectPlan = (plan) => {
    if (!user) {
      toast.error('Please login first to subscribe');
      navigate('/login');
      return;
    }
    
    if (plan.id === 'free') {
      toast.success('You are already on the Free plan!');
      return;
    }
    
    setSelectedPlan(plan);
    setShowPaymentModal(true);
  };

  const handlePayment = () => {
    toast.success(`Redirecting to ${selectedGateway} for payment...`);
    // In production, this would redirect to actual payment gateway
    setTimeout(() => {
      toast.success('Payment simulation successful!');
      setShowPaymentModal(false);
    }, 2000);
  };

  const getColorClass = (color) => {
    const colors = {
      gray: 'from-gray-500 to-gray-600',
      blue: 'from-blue-500 to-blue-600',
      purple: 'from-purple-500 to-purple-600',
      indigo: 'from-indigo-500 to-indigo-600',
    };
    return colors[color] || colors.gray;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="max-w-7xl mx-auto text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Choose Your Plan
        </h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          Start free and scale as you grow. All plans include core features with no hidden fees.
        </p>
      </div>

      {/* Plans Grid */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className={`bg-white rounded-2xl shadow-lg border-2 overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
              plan.popular ? 'border-primary-500 ring-2 ring-primary-500 ring-opacity-20' : 'border-gray-100'
            }`}
          >
            {/* Popular Badge */}
            {plan.popular && (
              <div className="bg-primary-500 text-white text-xs font-semibold py-1 px-3 text-center">
                MOST POPULAR
              </div>
            )}

            <div className="p-6">
              {/* Plan Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${getColorClass(plan.color)} flex items-center justify-center`}>
                  <plan.icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>
                  <p className="text-sm text-gray-500">{plan.description}</p>
                </div>
              </div>

              {/* Price */}
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900">${plan.price}</span>
                <span className="text-gray-500">/{plan.period}</span>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-600">{feature}</span>
                  </li>
                ))}
                {plan.notIncluded.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 opacity-50">
                    <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center text-gray-400">×</span>
                    <span className="text-sm text-gray-400 line-through">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <button
                onClick={() => handleSelectPlan(plan)}
                className={`w-full py-3 px-4 rounded-xl font-semibold transition-all duration-200 ${
                  plan.popular
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                }`}
              >
                {plan.id === 'free' ? 'Get Started Free' : 'Subscribe Now'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Payment Methods */}
      <div className="max-w-4xl mx-auto mt-16">
        <h3 className="text-center text-lg font-semibold text-gray-900 mb-6">
          Supported Payment Methods
        </h3>
        <div className="flex flex-wrap justify-center gap-4">
          {paymentGateways.map((gateway) => (
            <div
              key={gateway.id}
              className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm border border-gray-200"
            >
              <div className={`w-3 h-3 rounded-full ${gateway.color}`}></div>
              <span className="text-sm font-medium text-gray-700">{gateway.name}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Subscribe to {selectedPlan.name}
            </h3>
            <p className="text-gray-600 mb-6">
              ${selectedPlan.price}/{selectedPlan.period}
            </p>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Payment Method
              </label>
              <div className="grid grid-cols-2 gap-3">
                {paymentGateways.map((gateway) => (
                  <button
                    key={gateway.id}
                    onClick={() => setSelectedGateway(gateway.id)}
                    className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                      selectedGateway === gateway.id
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className={`w-3 h-3 rounded-full ${gateway.color}`}></div>
                    <span className="text-sm font-medium">{gateway.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowPaymentModal(false)}
                className="flex-1 py-3 px-4 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handlePayment}
                className="flex-1 py-3 px-4 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700"
              >
                Pay Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Plans;
